import React from "react";

import {
  Box,
  Button,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useTheme,
  Avatar,
  IconButton,
  Chip,
} from "@mui/material";
import { Comment, ErrorOutline, Download, AttachFile } from "@mui/icons-material";

import { IAcitvityCommentsLogsProps } from "./Activity.types";
import { ERROR_MESSAGES } from "../../constants/enums";
import { useDispatchWrapper } from "../../hooks";

import { IComment } from "../../redux/comments/commentsTypes";
import {
  fetchCommentsAysnc,
  upsertCommentAysnc,
} from "../../redux/comments/commentsActions";
import { CustomTextField } from "../formsComponents";
import { useFormik } from "formik";
import { useTimezoneFormatter } from "../../hooks/useTimezoneFormatter";
import { IFileUpload } from "../FileUpload/FileUpload.type";
import { uploadFile } from "../FileUpload/utils/uploadFile";
import { downLoadFile, previewFile } from "../FileUpload/utils";
import { showMessage } from "../../redux/messages/messagesActions";

export const ActivityCommentsLogs: React.FC<IAcitvityCommentsLogsProps> = (
  props,
) => {
  const { moduleInformation } = props;
  const [comments, setComments] = React.useState<IComment[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [attachmentFiles, setAttachmentFiles] = React.useState<IFileUpload[]>([]);
  const [uploadingFiles, setUploadingFiles] = React.useState(false);

  const theme = useTheme();
  const dispatch = useDispatchWrapper();
  const keyName = moduleInformation.commentKeyName || "module_uuid";

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const newFiles: IFileUpload[] = filesArray.map((file) => ({
        key: file.name + Date.now(),
        file: file,
        name: file.name,
        path: "",
      }));
      setAttachmentFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (key: string) => {
    setAttachmentFiles((prev) => prev.filter((f) => f.key !== key));
  };

  const { values, handleChange, handleSubmit, resetForm } = useFormik({
    initialValues: {
      comment_remark: "",
    },
    onSubmit: async (values) => {
      setUploadingFiles(true);
      try {
        const uploadedPaths = await Promise.all(
          attachmentFiles.map(async (f) => {
            if (f.file) {
              const path = await uploadFile({
                file: f.file,
                module_name: "USER",
                defaultReturn: "",
                asPayload: {},
                onWarning: (message) => {
                  dispatch(
                    showMessage({
                      type: "warning",
                      message: message,
                      displayAs: "snackbar",
                    })
                  );
                },
              });
              return path;
            }
            return f.path || "";
          })
        );

        const attachmentsArray = uploadedPaths.filter((p) => p !== "");

        const commentPayload: any = {
          comment_t_uuid: null,
          record_uuid: keyName === "record_uuid" ? moduleInformation.moduleId : "",
          comment_remark: values.comment_remark.trim(),
          attachments: attachmentsArray,
          status: "ACTIVE",
        };

        dispatch(
          upsertCommentAysnc(
            commentPayload,
            (isSuccess) => {
              if (isSuccess) {
                resetForm();
                setAttachmentFiles([]);
                fetchComments();
              }
              setUploadingFiles(false);
            },
          ),
        );
      } catch (err: any) {
        dispatch(
          showMessage({
            type: "error",
            message: err.message || "Failed to upload files",
            displayAs: "snackbar",
          })
        );
        setUploadingFiles(false);
      }
    },
  });

  const fetchComments = async () => {
    setLoading(true);
    dispatch(
      fetchCommentsAysnc(
        moduleInformation.moduleId,
        (isSuccess, data) => {
          if (isSuccess && data) {
            setComments(data);
          }
          setLoading(false);
        },
        undefined,
        keyName,
      ),
    );
  };

  React.useEffect(() => {
    if (moduleInformation.moduleId) {
      fetchComments();
    }
  }, [moduleInformation.moduleId, keyName]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", pt: 2, px: 1 }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Comments
      </Typography>
      <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, mb: 2 }}>
        {loading && (
          <Stack spacing={1.5}>
            <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1.5 }} />
            <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1.5 }} />
            <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1.5 }} />
          </Stack>
        )}
        {!loading && comments.length === 0 && (
          <Box
            sx={{
              py: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Comment sx={{ fontSize: 40, color: theme.palette.text.disabled }} />
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              No comments yet. Be the first to say something!
            </Typography>
          </Box>
        )}
        {!loading &&
          comments.map((item, index) => (
            <Row key={index} comment={item} />
          ))}
      </Box>
      <Box
        sx={{
          pt: 2,
          pb: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {attachmentFiles.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
            {attachmentFiles.map((file) => (
              <Chip
                key={file.key}
                label={file.name}
                onDelete={() => handleRemoveFile(file.key)}
                size="small"
              />
            ))}
          </Box>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <IconButton
                component="label"
                color="primary"
                disabled={uploadingFiles}
              >
                <AttachFile />
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={handleFileChange}
                />
              </IconButton>
            </Grid>
            <Grid item xs>
              <CustomTextField
                fullWidth
                id="comment_remark"
                value={values.comment_remark}
                sx={{
                  background: "#fff",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "20px",
                  },
                }}
                onChange={handleChange}
                placeholder="Add a comment..."
                size="small"
                disabled={uploadingFiles}
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                size="medium"
                type="submit"
                disabled={(values.comment_remark.trim().length < 1 && attachmentFiles.length === 0) || uploadingFiles}
                sx={{
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                }}
              >
                {uploadingFiles ? "Posting..." : "Post"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Box>
  );
};

const Row: React.FC<{ comment: IComment }> = ({ comment }) => {
  const theme = useTheme();
  const { ftDateTime } = useTimezoneFormatter();

  const stringToColor = (string: string) => {
    let hash = 0;
    let i;
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };

  const name = comment.created_by_name || "User";
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  let atts: (string | null)[] = [];
  if (comment.attachments) {
    if (Array.isArray(comment.attachments)) {
      atts = comment.attachments;
    } else if (typeof comment.attachments === "string") {
      try {
        atts = JSON.parse(comment.attachments);
      } catch {
        atts = [comment.attachments];
      }
    }
  }

  const validAtts = atts.filter((url): url is string => typeof url === "string" && url !== "");

  return (
    <Box sx={{ display: "flex", gap: 2, mb: 2.5, alignItems: "flex-start" }}>
      <Avatar
        sx={{
          bgcolor: stringToColor(name),
          width: 36,
          height: 36,
          fontSize: "0.95rem",
          fontWeight: 600,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        {initials}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: theme.palette.text.primary }}>
            {name}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {ftDateTime(comment.insert_ts ?? "")}
          </Typography>
        </Box>
        <Box
          sx={{
            bgcolor: theme.palette.action.hover,
            borderRadius: "0px 12px 12px 12px",
            p: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            maxWidth: "fit-content",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          {comment.comment_remark && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                lineHeight: 1.5,
              }}
            >
              {comment.comment_remark}
            </Typography>
          )}

          {validAtts.length > 0 && (
            <Box sx={{ mt: comment.comment_remark ? 1.5 : 0, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {validAtts.map((url, i) => (
                <AttachmentItem key={i} url={url} index={i} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const AttachmentItem: React.FC<{ url: string; index: number }> = ({ url, index }) => {
  const dispatch = useDispatchWrapper();
  const [loading, setLoading] = React.useState(false);
  const fileName = url.split("/").pop() || `Attachment ${index + 1}`;

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      setLoading(true);
      await downLoadFile(url);
    } catch (err) {
      dispatch(
        showMessage({
          displayAs: "snackbar",
          message: "Something went wrong during download!",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      setLoading(true);
      await previewFile(url);
    } catch (err) {
      dispatch(
        showMessage({
          displayAs: "snackbar",
          message: "Something went wrong during preview!",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "16px",
        pl: 1,
        pr: 0.5,
        py: 0.25,
        maxWidth: "220px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
      }}
    >
      {/* <AttachFile sx={{ fontSize: 16, color: "primary.main" }} /> */}
      <Typography
        variant="caption"
        onClick={handlePreview}
        sx={{
          cursor: "pointer",
          fontWeight: 500,
          color: "primary.main",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          "&:hover": {
            textDecoration: "underline",
          },
        }}
        title={fileName}
      >
        <AttachFile sx={{ fontSize: "0.95rem" }} />
        {fileName}
      </Typography>
      <IconButton
        size="small"
        onClick={handleDownload}
        disabled={loading}
        sx={{ p: 0.25, ml: 0.5 }}
      >
        <Download fontSize="small" sx={{ width: 14, height: 14 }} />
      </IconButton>
    </Box>
  );
};
